import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { environment } from 'environments/environment';
import { GrowlerService } from '../components/shared/growler/growler.service';
import { ClientErrorLoggingService } from './client-error-logging.service';

const GENERIC_USER_MESSAGE = 'An unexpected error occurred. Please try again.';

/**
 * Generic, framework-level noise that is NOT actionable by the user and must
 * never produce a growler or a backend report. These fire routinely in healthy
 * apps (benign layout-measurement loops, stale-bundle chunk loads after a
 * deploy, dynamic-import hiccups) — surfacing them would degrade UX, not
 * protect it.
 */
const BENIGN_ERROR_PATTERNS: readonly RegExp[] = [
    /ResizeObserver loop/i,
    /ChunkLoadError/i,
    /Loading chunk [\w-]+ failed/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Loading CSS chunk/i,
    // mat-icon SVG fetch blips (asset 404 / transient network) — the icon just
    // doesn't render; not user-actionable. e.g. "Error retrieving icon :close!
    // Http failure response for /assets/.../icon_close.svg: 0 Unknown Error" (SD-7619).
    /Error retrieving icon/i,
];

/**
 * Minimum gap between user-facing growlers. A burst of errors (e.g. a render
 * loop throwing every tick) must never carpet the screen with popups — the
 * user sees at most one calm generic message per window.
 */
const GROWLER_THROTTLE_MS = 5000;

/**
 * Window during which an identical error message is reported to the backend
 * only once, so an error storm cannot hammer the logging endpoint.
 */
const REPORT_DEDUPE_MS = 10000;

/**
 * Application-wide error handler (SD-7544 P1b).
 *
 * It is a BACKSTOP for genuinely uncaught errors only. Responsibilities:
 *   1. Show the user a GENERIC growler — never the raw error message/stack —
 *      filtered for benign noise, throttled so bursts can't spam.
 *   2. Report full error detail to the backend (Better Stack), deduped.
 *   3. Echo the error to the console for local dev visibility (console output
 *      is globally suppressed in production; see main.ts).
 *
 * It deliberately ignores {@link HttpErrorResponse}: API errors are already
 * handled+messaged by components/interceptors and logged server-side, so
 * re-growling them here would double-message the user.
 *
 * `handleError` is wrapped in try/catch so it can NEVER itself throw (a throw
 * here would re-enter Angular's error handling and risk an infinite loop).
 *
 * DI note: ErrorHandler is constructed very early in bootstrap, before the
 * HTTP_INTERCEPTORS / ApplicationHttpClient provider graph is fully realised.
 * Constructor-injecting those would risk a circular/early-resolution failure,
 * so we inject only the {@link Injector} and resolve collaborators lazily on
 * first use inside `handleError`.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler
{
    private _lastGrowlerAt = 0;
    private readonly _recentReports = new Map<string, number>();

    constructor(private _injector: Injector) {}

    public handleError(error: unknown): void {
        try {
            // API errors are handled and messaged elsewhere (components /
            // interceptors) and already logged server-side. The backstop must
            // not double-message the user.
            if (error instanceof HttpErrorResponse) {
                this.logLocally(error);
                return;
            }

            // Thrown non-Error primitives carry no actionable detail (no message,
            // no stack). They come from benign navigation cancellations — a route
            // guard/resolver rejecting with `false`, a Promise/observable rejected
            // with no reason (`undefined`) — not genuine uncaught JS errors. Before
            // this filter they flooded Better Stack as "[ClientError] undefined" /
            // "[ClientError] false" and popped a generic growler on harmless
            // redirects. Dev console only — no growler, no report (SD-7600).
            if (this.isNonActionable(error)) {
                this.logLocally(error);
                return;
            }

            const detail = this.extractDetail(error);

            if (this.isBenign(detail.message)) {
                // Benign framework noise: dev console only, no growler/report.
                this.logLocally(error);
                return;
            }

            // The user-facing growler + backend report run on every DEPLOYED
            // environment (gated on environment.useProdMode: prod, uat, staging,
            // qa) — not just prod — so the masking path is exercised on
            // staging/QA and QA can validate it. The user sees only a safe
            // generic message; full detail ships to Better Stack. In local dev
            // and unit tests (useProdMode === false) the handler performs NO
            // side effects (no growler, no HTTP) and the full error goes to the
            // dev console, so registering it app-wide can't interfere with the
            // test suite. Note: browser-console suppression is a stricter,
            // prod-only concern handled separately in main.ts.
            if (this.shouldMaskAndReport()) {
                this.maybeShowGenericGrowler();
                this.maybeReportToBackend(detail);
            }

            this.logLocally(error);
        } catch {
            // Last-resort guard: error handling must never throw. Intentionally
            // swallow — we cannot safely re-enter the error pipeline here.
        }
    }

    private extractDetail(error: unknown): {
        message: string;
        name?: string;
        stack?: string;
    } {
        if (error instanceof Error) {
            return {
                message: error.message || error.name || 'Unknown error',
                name: error.name,
                stack: error.stack,
            };
        }

        const stringified = String(error);

        return { message: stringified.trim() !== '' ? stringified : 'Unknown error' };
    }

    private isBenign(message: string): boolean {
        return BENIGN_ERROR_PATTERNS.some((pattern) => pattern.test(message));
    }

    /**
     * A thrown value with no actionable detail — `null`, `undefined`, or a bare
     * boolean (e.g. a cancelled route guard/resolver). These are navigation
     * noise, not genuine uncaught errors, and must not growler or report.
     */
    private isNonActionable(error: unknown): boolean {
        return error === null || error === undefined || typeof error === 'boolean';
    }

    /**
     * Show a single generic growler, throttled so a burst of errors cannot
     * spam the user with popups.
     */
    private maybeShowGenericGrowler(): void {
        const now = Date.now();

        if (now - this._lastGrowlerAt < GROWLER_THROTTLE_MS) {
            return;
        }

        this._lastGrowlerAt = now;

        try {
            this._injector.get(GrowlerService).oops(GENERIC_USER_MESSAGE);
        } catch {
            // Growler unavailable (e.g. very early bootstrap) — ignore.
            this._lastGrowlerAt = 0;
        }
    }

    /**
     * Report full detail to the backend, skipping identical messages seen in
     * the dedupe window so an error storm can't hammer the endpoint.
     */
    private maybeReportToBackend(detail: { message: string; name?: string; stack?: string }): void {
        const now = Date.now();
        const lastReportedAt = this._recentReports.get(detail.message);

        if (lastReportedAt !== undefined && now - lastReportedAt < REPORT_DEDUPE_MS) {
            return;
        }

        this.pruneRecentReports(now);
        this._recentReports.set(detail.message, now);

        try {
            this._injector.get(ClientErrorLoggingService).logClientError({
                message: detail.message,
                name: detail.name,
                stack: detail.stack,
                url: typeof window !== 'undefined' ? window.location.href : undefined,
            }).subscribe({
                // The service already swallows failures, but guard the subscribe
                // too so a stream error can never escape as an unhandled async error.
                error: () => undefined,
            });
        } catch {
            // Reporting service unavailable — never let this surface.
        }
    }

    /**
     * Whether to show the generic growler and report to the backend. Gated on
     * `useProdMode` so masking is active on every deployed environment (prod,
     * uat, staging, qa) and stays off in local dev / unit tests. A single
     * indirection over the static `environment` import so it can be stubbed in
     * unit tests without mutating shared global state.
     */
    private shouldMaskAndReport(): boolean {
        return environment.useProdMode === true;
    }

    /**
     * Keep the dedupe map small — drop entries older than the dedupe window.
     */
    private pruneRecentReports(now: number): void {
        for (const [message, reportedAt] of this._recentReports) {
            if (now - reportedAt >= REPORT_DEDUPE_MS) {
                this._recentReports.delete(message);
            }
        }
    }

    private logLocally(error: unknown): void {
        try {
            // console output is globally suppressed in production (see main.ts),
            // so this is a no-op in prod and a normal console.error in dev.
            console.error(error);
        } catch {
            // Ignore — logging must never break error handling.
        }
    }
}
